import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import type { Group, User } from "@domaddress/shared";
import GroupCard from "../components/GroupCard";
import GroupListRow from "../components/GroupListRow";
import GroupFormModal from "../components/GroupFormModal";
import ViewModeToggle from "../components/ViewModeToggle";
import PaginationBar from "../components/PaginationBar";
import { useViewMode } from "../hooks/useViewMode";

export default function GroupsPage() {
  const [viewMode, setViewMode] = useViewMode("groups");
  const [groups, setGroups] = useState<
    (Group & { can_manage?: boolean; view_access?: "full" | "directory" })[]
  >([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Group | null>(null);
  const [me, setMe] = useState<User | null>(null);

  const canCreateGroup =
    me &&
    (me.is_super_admin || me.app_role === "secretaris" || me.app_role === "beheerder");

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ data: User }>("/api/me");
        setMe(r.data.data);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, perPage]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (search.trim()) params.search = search.trim();
      const res = await api.get("/api/groups", { params });
      setGroups(res.data.data);
      setTotal(typeof res.data.total === "number" ? res.data.total : res.data.data.length);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  async function handleDelete(id: number) {
    if (!confirm("Deze groep permanent verwijderen? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await api.delete(`/api/groups/${id}`);
      await fetchGroups();
    } catch {
      window.alert("Verwijderen mislukt. Mogelijk ontbreekt toestemming, of probeer het opnieuw.");
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Groepen</h2>
          <p className="page-subtitle">
            {loading ? "…" : `${total} ${total === 1 ? "groep" : "groepen"}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            label="Weergave"
            tilesLabel="Tegels"
            listLabel="Lijst"
          />
          {canCreateGroup && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              <span>+</span> Nieuwe groep
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="filters-start">
          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Zoek op naam of omschrijving…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {loading ? (
        viewMode === "tiles" ? (
          <div className="groups-grid">
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        ) : (
          <div className="loading-list">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        )
      ) : total === 0 ? (
        <div className="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p>Nog geen groepen</p>
          {canCreateGroup && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              Eerste groep maken
            </button>
          )}
        </div>
      ) : (
        <>
          {groups.length === 0 ? (
            <div className="empty-state">
              <p>Geen groepen op deze pagina. Kies een andere pagina of wis het zoekveld.</p>
              <button type="button" className="btn-secondary" onClick={() => setPage(1)}>
                Eerste pagina
              </button>
            </div>
          ) : viewMode === "tiles" ? (
            <div className="groups-grid">
              {groups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  onEdit={() => { setEditing(group); setShowForm(true); }}
                  onDelete={() => handleDelete(group.id)}
                />
              ))}
            </div>
          ) : (
            <div className="data-list">
              {groups.map((group) => (
                <GroupListRow
                  key={group.id}
                  group={group}
                  onEdit={() => { setEditing(group); setShowForm(true); }}
                  onDelete={() => handleDelete(group.id)}
                />
              ))}
            </div>
          )}
          <PaginationBar
            page={page}
            perPage={perPage}
            total={total}
            itemLabel="groepen"
            onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </>
      )}

      {showForm && (
        <GroupFormModal
          group={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchGroups(); }}
        />
      )}
    </div>
  );
}
